import { useQueryClient, UseMutationResult } from '@tanstack/react-query'
import { useAuthFetchWithResponse } from '@e3dc-react/shell/Hooks/AuthFetch'
import { usePortalMutation } from 'src/Libs/api'
import { updateVehicleSettings, UpdateVehicleSettingsPayload } from '../api/vehicle-settings.api'
import { getUserVehiclesQueryKey } from './use-user-vehicles'
import { getVehicleImageQueryKey } from './use-vehicle-image'
import { useSnackbar } from '@e3dc-react/shell/Hooks/Snackbar'
import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'

export interface UpdateVehicleSettingsVariables {
  installationId: number
  vehicleId: number
  payload: UpdateVehicleSettingsPayload
}

type UseUpdateVehicleSettings = UseMutationResult<boolean | null, Error, UpdateVehicleSettingsVariables>

/**
 * Hook for updating vehicle settings (custom name and custom image).
 * Calls onSuccess callback after a successful update.
 */
export const useUpdateVehicleSettings = (installationId: number | undefined, onSuccess?: () => void): UseUpdateVehicleSettings => {
  const authFetch = useAuthFetchWithResponse()
  const queryClient = useQueryClient()
  const { enqueueErrorStack, enqueueSuccessStack } = useSnackbar()
  const { t } = useCustomTranslation()

  return usePortalMutation<boolean, Error, UpdateVehicleSettingsVariables>({
    mutationFn: ({ installationId, vehicleId, payload }) => updateVehicleSettings(installationId, vehicleId, payload, authFetch),
    onSuccess: (_, variables) => {
      const vehicleIdStr = variables.vehicleId.toString()
      const installationIdStr = variables.installationId.toString()
      queryClient.invalidateQueries({ queryKey: getUserVehiclesQueryKey(variables.installationId) })
      queryClient.invalidateQueries({ queryKey: getVehicleImageQueryKey(installationIdStr, vehicleIdStr) })
      enqueueSuccessStack(t('vehicle-integration.settings-modal.update-success'))
      onSuccess?.()
    },
    onError() {
      enqueueErrorStack(t('vehicle-integration.settings-modal.update-error'))
    },
  })
}
